import {
  Children,
  Fragment,
  isValidElement,
  type ComponentType,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from "react";
import { z } from "zod/v4";

type SlotProps = { children?: ReactNode };

const displayNameSchema = z.string().min(1).optional().catch(undefined);

/** forwardRef/memo wrappers: an object carrying an optional displayName and the wrapped component. */
const exoticComponentSchema = z.object({
  displayName: displayNameSchema,
  render: z.custom<ComponentType>((value) => value instanceof Function).optional(),
});

/** Prefer `displayName` over reference equality so slot markers still match after Fast Refresh (new function identity). */
function getSlotDisplayName(type: ReactElement["type"]): string | undefined {
  if (type instanceof Function) {
    return "displayName" in type ? displayNameSchema.parse(type.displayName) : undefined;
  }
  const exotic = exoticComponentSchema.safeParse(type);
  if (!exotic.success) return undefined;
  if (exotic.data.displayName) return exotic.data.displayName;
  return exotic.data.render ? getSlotDisplayName(exotic.data.render) : undefined;
}

function isElementType(element: ReactElement, targetType: ElementType): boolean {
  if (element.type === targetType) return true;
  const targetName = getSlotDisplayName(targetType);
  if (targetName == null) return false;
  return getSlotDisplayName(element.type) === targetName;
}

function flattenChildren(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) => {
    if (isValidElement<SlotProps>(child) && child.type === Fragment) {
      return flattenChildren(child.props.children);
    }
    return [child];
  });
}

/**
 * Collects the `children` of every **direct** child whose type equals `targetType` (fragments flattened).
 * Does not look inside other components — use {@link extractReactChildrenByTypeDeep} for nested markers
 * (e.g. `<MainAction>` inside `<Actions>`).
 */
export function extractReactChildrenByType(
  children: ReactNode,
  targetType: ElementType,
): ReactNode[] {
  const matches: ReactNode[] = [];
  for (const child of flattenChildren(children)) {
    if (isValidElement<SlotProps>(child) && isElementType(child, targetType)) {
      const slot = child.props.children;
      if (slot !== false) {
        matches.push(slot);
      }
    }
  }
  return matches;
}

/**
 * Depth-first walk: collects every element whose type equals `targetType` (fragments flattened),
 * in document order. Unlike the children-extracting variants this returns the matched elements
 * themselves, so their own props (className etc.) survive relocation to a different render slot.
 */
export function extractReactElementsByTypeDeep(
  children: ReactNode,
  targetType: ElementType,
): ReactElement[] {
  const matches: ReactElement[] = [];

  function visit(node: ReactNode): void {
    if (node == null) {
      return;
    }
    if (Array.isArray(node)) {
      for (const n of node) {
        visit(n);
      }
      return;
    }
    if (!isValidElement<SlotProps>(node)) {
      return;
    }
    if (node.type === Fragment) {
      visit(node.props.children);
      return;
    }
    if (isElementType(node, targetType)) {
      matches.push(node);
      return;
    }
    visit(node.props.children);
  }

  visit(children);
  return matches;
}

/**
 * Depth-first walk: collects `children` of every element whose type equals `targetType`, in document order.
 * Does not descend into a matched node (inner content is the extracted slot).
 */
export function extractReactChildrenByTypeDeep(
  children: ReactNode,
  targetType: ElementType,
): ReactNode[] {
  const matches: ReactNode[] = [];

  function visit(node: ReactNode): void {
    if (node == null) {
      return;
    }
    if (Array.isArray(node)) {
      for (const n of node) {
        visit(n);
      }
      return;
    }
    if (!isValidElement<SlotProps>(node)) {
      return;
    }
    if (node.type === Fragment) {
      visit(node.props.children);
      return;
    }
    if (isElementType(node, targetType)) {
      const slot = node.props.children;
      if (slot !== false) {
        matches.push(slot);
      }
      return;
    }
    visit(node.props.children);
  }

  visit(children);
  return matches;
}
